// src/pages/library/LibraryForm.tsx
import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    FormControlLabel,
    Grid,
    IconButton,
    MenuItem,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import {
    LIBRARY_TIER_LABELS,
    LibraryItemType,
    LibrarySection,
    LibraryTier,
    LibraryType,
} from '../../types/library';
import {
    useCreateLibrary,
    useLibrary,
    useUpdateLibrary,
    useUpdateTier,
} from '../../hooks/useLibrary.ts';
import { useLibrarySections } from '../../hooks/useLibrarySection.ts';
import { CreateLibraryRequest } from '../../models/library/createLibraryRequest.ts';
import { UpdateLibraryRequest } from '../../models/library/updateLibraryRequest.ts';
import { ImageUpload } from '../common/ImageUpload.tsx';
import { LibraryThumbnailPanel } from './LibraryThumbnailPanel.tsx';
import { useWordbook } from '../../hooks/useWordbook.ts';
import { useWordbookGroup } from '../../hooks/useWordbookGroup.ts';

interface FormData {
    itemType: LibraryItemType;
    wordbookId?: number;
    groupId?: number;
    headLang: string;
    meaningLang: string;
    categorySectionId: number;
    description: string;
    libraryType: LibraryType;
    thumbnailUrl?: string;
    isVisible: boolean;
    tier?: LibraryTier;
    /** 큐레이션 (Edit 전용): 0 = (없음) → clearCuration */
    curationSectionId: number;
    curationSortOrder: number | null;
}

const LANGUAGE_OPTIONS = [
    { value: 'ko', label: '한국어' },
    { value: 'en', label: '영어' },
    { value: 'ja', label: '일본어' },
    { value: 'zh', label: '중국어' },
    { value: 'es', label: '스페인어' },
    { value: 'fr', label: '프랑스어' },
    { value: 'de', label: '독일어' },
];

const sectionDisplayName = (s: LibrarySection): string => {
    const ko = s.translations.find((t) => t.lang === 'ko')?.displayName;
    return ko ?? s.code;
};

export const LibraryForm: FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const isEditMode = !!id;
    const libraryId = id ? parseInt(id) : 0;

    // Create 모드: URL 파라미터에서 wordbookId, groupId 가져오기
    const urlWordbookId = searchParams.get('wordbookId');
    const urlGroupId = searchParams.get('groupId');

    const [itemType, setItemType] = useState<LibraryItemType>(
        urlWordbookId
            ? LibraryItemType.SINGLE
            : urlGroupId
              ? LibraryItemType.GROUP
              : LibraryItemType.SINGLE
    );

    // Edit 모드: 기존 라이브러리 데이터 로드
    const { data: library, isLoading: libraryLoading } = useLibrary(libraryId);

    // Create 모드: 단어장 또는 그룹 정보 가져오기
    const { data: wordbook } = useWordbook(
        urlWordbookId ? parseInt(urlWordbookId) : 0
    );
    const { data: group } = useWordbookGroup(
        urlGroupId ? parseInt(urlGroupId) : 0
    );

    const createLibrary = useCreateLibrary();
    const updateLibrary = useUpdateLibrary();
    const updateTier = useUpdateTier();
    const { data: categorySections = [] } = useLibrarySections('CATEGORY');
    const { data: curationSections = [] } = useLibrarySections('CURATION');

    const sortedCategorySections = useMemo(
        () =>
            [...categorySections].sort(
                (a, b) => a.sortOrder - b.sortOrder || a.id - b.id
            ),
        [categorySections]
    );
    const sortedCurationSections = useMemo(
        () =>
            [...curationSections].sort(
                (a, b) => a.sortOrder - b.sortOrder || a.id - b.id
            ),
        [curationSections]
    );

    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        watch,
        reset,
    } = useForm<FormData>({
        defaultValues: {
            itemType: itemType,
            wordbookId: urlWordbookId ? parseInt(urlWordbookId) : undefined,
            groupId: urlGroupId ? parseInt(urlGroupId) : undefined,
            headLang: 'en',
            meaningLang: 'ko',
            categorySectionId: 0,
            libraryType: LibraryType.SYSTEM,
            isVisible: true,
            tier: LibraryTier.FREE,
            curationSectionId: 0,
            curationSortOrder: null,
        },
    });

    const thumbnailUrl = watch('thumbnailUrl');

    // Edit 모드: 기존 데이터로 폼 초기화
    useEffect(() => {
        if (isEditMode && library) {
            reset({
                itemType: library.itemType,
                wordbookId: library.wordbookId,
                groupId: library.groupId,
                headLang: library.headLang,
                meaningLang: library.meaningLang,
                categorySectionId: library.categorySection?.id ?? 0,
                description: library.description,
                libraryType: library.libraryType,
                thumbnailUrl: library.thumbnailUrl || '',
                isVisible: library.isVisible,
                tier:
                    (library as { tier?: LibraryTier }).tier ?? LibraryTier.FREE,
                curationSectionId: library.curationSection?.id ?? 0,
                curationSortOrder: library.curationSortOrder ?? null,
            });
            setItemType(library.itemType);
        }
    }, [isEditMode, library, reset]);

    // Create 모드: 단어장 정보로 자동 채우기
    useEffect(() => {
        if (!isEditMode && wordbook) {
            setValue('headLang', wordbook.headLang);
            setValue('meaningLang', wordbook.meaningLang);
            setValue('thumbnailUrl', wordbook.thumbnailUrl || '');
        }
    }, [isEditMode, wordbook, setValue]);

    // Create 모드: 그룹 정보로 자동 채우기
    useEffect(() => {
        if (!isEditMode && group) {
            setValue('headLang', group.headLang);
            setValue('meaningLang', group.meaningLang);
            setValue('thumbnailUrl', group.thumbnailUrl || '');
        }
    }, [isEditMode, group, setValue]);

    const onSubmit = async (data: FormData) => {
        try {
            if (!data.categorySectionId || data.categorySectionId <= 0) {
                alert('카테고리 섹션을 선택해주세요.');
                return;
            }
            if (isEditMode) {
                // Edit 모드: 업데이트
                const clearCuration =
                    !data.curationSectionId || data.curationSectionId <= 0;
                const request: UpdateLibraryRequest = {
                    categorySectionId: data.categorySectionId,
                    description: data.description,
                    isVisible: data.isVisible,
                    clearCuration,
                };
                if (!clearCuration) {
                    request.curationSectionId = data.curationSectionId;
                    request.curationSortOrder = data.curationSortOrder ?? null;
                }

                await updateLibrary.mutateAsync({
                    id: libraryId,
                    data: request,
                });

                if (
                    data.tier &&
                    data.tier !==
                        (library as { tier?: LibraryTier } | undefined)?.tier
                ) {
                    await updateTier.mutateAsync({
                        id: libraryId,
                        tier: data.tier,
                    });
                }

                navigate(`/library/${libraryId}`);
            } else {
                // Create 모드: 생성
                const clearCuration =
                    !data.curationSectionId || data.curationSectionId <= 0;
                const request: CreateLibraryRequest = {
                    wordbookId:
                        itemType === LibraryItemType.SINGLE
                            ? data.wordbookId
                            : undefined,
                    groupId:
                        itemType === LibraryItemType.GROUP
                            ? data.groupId
                            : undefined,
                    headLang: data.headLang,
                    meaningLang: data.meaningLang,
                    description: data.description,
                    categorySectionId: data.categorySectionId,
                    curationSectionId: clearCuration
                        ? undefined
                        : data.curationSectionId,
                    curationSortOrder: clearCuration
                        ? undefined
                        : data.curationSortOrder ?? undefined,
                    clearCuration,
                    libraryType: data.libraryType,
                    thumbnailUrl: data.thumbnailUrl || undefined,
                    isVisible: data.isVisible,
                };

                await createLibrary.mutateAsync(request);
                navigate('/library');
            }
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : '실패';
            alert(`${isEditMode ? '수정' : '생성'} 실패: ${message}`);
        }
    };

    const handleCancel = () => {
        navigate(isEditMode ? `/library/${libraryId}` : '/library');
    };

    if (isEditMode && libraryLoading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="80vh"
            >
                <CircularProgress />
            </Box>
        );
    }

    if (isEditMode && !libraryLoading && !library) {
        return (
            <Box p={3}>
                <Alert severity="error">라이브러리를 불러올 수 없습니다.</Alert>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={() => navigate('/library')}
                    sx={{ mt: 2 }}
                >
                    목록으로
                </Button>
            </Box>
        );
    }

    const mutation = isEditMode ? updateLibrary : createLibrary;

    return (
        <Box p={3}>
            {/* 헤더 */}
            <Box display="flex" alignItems="center" gap={2} mb={3}>
                <IconButton onClick={handleCancel}>
                    <ArrowBack />
                </IconButton>
                <Typography variant="h4">
                    {isEditMode
                        ? `라이브러리 수정: ${library?.title}`
                        : '라이브러리 등록'}
                </Typography>
            </Box>

            {/* Create 모드: 자동 입력 안내 */}
            {!isEditMode && (wordbook || group) && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    {wordbook && (
                        <>
                            <strong>단어장:</strong> {wordbook.title} (
                            {wordbook.headLang} → {wordbook.meaningLang})
                            <br />
                            단어 수: {wordbook.totalWords.toLocaleString()}개
                        </>
                    )}
                    {group && (
                        <>
                            <strong>그룹:</strong> {group.title} ({group.headLang} →{' '}
                            {group.meaningLang})
                            <br />
                            단어장 수: {group.totalWordbooks}개 | 총 단어 수:{' '}
                            {group.totalWords.toLocaleString()}개
                        </>
                    )}
                </Alert>
            )}

            {/* Edit 모드: 기본 정보 (읽기 전용) */}
            {isEditMode && library && (
                <Grid container spacing={3} mb={3}>
                    <Grid size={{ xs: 12 }}>
                        <Card>
                            <CardContent>
                                <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                    gutterBottom
                                >
                                    라이브러리 정보
                                </Typography>
                                <Box display="flex" gap={2} flexWrap="wrap">
                                    <Chip label={`ID: ${library.id}`} />
                                    <Chip
                                        label={
                                            library.itemType ===
                                            LibraryItemType.GROUP
                                                ? '그룹'
                                                : '단일'
                                        }
                                        color="primary"
                                    />
                                    {library.wordbookId && (
                                        <Chip
                                            label={`단어장 ID: ${library.wordbookId}`}
                                        />
                                    )}
                                    {library.groupId && (
                                        <Chip
                                            label={`그룹 ID: ${library.groupId}`}
                                        />
                                    )}
                                    <Chip
                                        label={`단어 수: ${library.wordCount.toLocaleString()}`}
                                    />
                                    <Chip
                                        label={`다운로드: ${library.downloadCount.toLocaleString()}`}
                                    />
                                    <Chip
                                        label={`평점: ${library.ratingAverage.toFixed(
                                            1
                                        )} (${library.ratingCount})`}
                                    />
                                </Box>
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    미리보기 단어는 서버에서 자동으로 추출됩니다 (최대
                                    10개)
                                </Alert>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={3}>
                    {/* Create 모드에서만: 언어 설정 */}
                    {!isEditMode && (
                        <Grid size={{ xs: 12 }}>
                            <Card>
                                <CardContent>
                                    <Typography
                                        variant="subtitle1"
                                        gutterBottom
                                        fontWeight="bold"
                                    >
                                        언어 설정
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                                select
                                                label="단어 언어"
                                                fullWidth
                                                {...register('headLang', {
                                                    required: '단어 언어는 필수입니다',
                                                })}
                                                error={!!errors.headLang}
                                                helperText={errors.headLang?.message}
                                                defaultValue="en"
                                            >
                                                {LANGUAGE_OPTIONS.map((option) => (
                                                    <MenuItem
                                                        key={option.value}
                                                        value={option.value}
                                                    >
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <TextField
                                                select
                                                label="의미 언어"
                                                fullWidth
                                                {...register('meaningLang', {
                                                    required: '의미 언어는 필수입니다',
                                                })}
                                                error={!!errors.meaningLang}
                                                helperText={errors.meaningLang?.message}
                                                defaultValue="ko"
                                            >
                                                {LANGUAGE_OPTIONS.map((option) => (
                                                    <MenuItem
                                                        key={option.value}
                                                        value={option.value}
                                                    >
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {/* Create 모드에서만: 라이브러리 타입 선택 */}
                    {!isEditMode && (
                        <Grid size={{ xs: 12 }}>
                            <Card>
                                <CardContent>
                                    <Typography
                                        variant="subtitle1"
                                        gutterBottom
                                        fontWeight="bold"
                                    >
                                        라이브러리 타입 선택
                                    </Typography>
                                    <ToggleButtonGroup
                                        value={itemType}
                                        exclusive
                                        onChange={(_, value) => {
                                            if (value) {
                                                setItemType(value);
                                                setValue('itemType', value);
                                            }
                                        }}
                                        fullWidth
                                        disabled={!!(urlWordbookId || urlGroupId)}
                                    >
                                        <ToggleButton value={LibraryItemType.SINGLE}>
                                            단일 단어장
                                        </ToggleButton>
                                        <ToggleButton value={LibraryItemType.GROUP}>
                                            그룹 단어장
                                        </ToggleButton>
                                    </ToggleButtonGroup>

                                    <Box mt={2}>
                                        {itemType === LibraryItemType.SINGLE ? (
                                            <TextField
                                                label="단어장 ID"
                                                type="number"
                                                fullWidth
                                                {...register('wordbookId', {
                                                    required: '단어장 ID는 필수입니다',
                                                    valueAsNumber: true,
                                                })}
                                                error={!!errors.wordbookId}
                                                helperText={errors.wordbookId?.message}
                                                disabled={!!urlWordbookId}
                                            />
                                        ) : (
                                            <TextField
                                                label="그룹 ID"
                                                type="number"
                                                fullWidth
                                                {...register('groupId', {
                                                    required: '그룹 ID는 필수입니다',
                                                    valueAsNumber: true,
                                                })}
                                                error={!!errors.groupId}
                                                helperText={errors.groupId?.message}
                                                disabled={!!urlGroupId}
                                            />
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {/* 기본 정보 */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card>
                            <CardContent>
                                <Typography
                                    variant="subtitle1"
                                    gutterBottom
                                    fontWeight="bold"
                                >
                                    기본 정보
                                </Typography>

                                <Controller
                                    name="categorySectionId"
                                    control={control}
                                    rules={{
                                        validate: (v) =>
                                            (typeof v === 'number' && v > 0) ||
                                            '카테고리 섹션은 필수입니다',
                                    }}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            select
                                            label="카테고리 섹션"
                                            fullWidth
                                            margin="normal"
                                            value={field.value || ''}
                                            onChange={(e) =>
                                                field.onChange(Number(e.target.value))
                                            }
                                            error={!!fieldState.error}
                                            helperText={
                                                fieldState.error?.message ??
                                                'library_sections (type=CATEGORY) 에서 선택'
                                            }
                                        >
                                            {sortedCategorySections.length === 0 && (
                                                <MenuItem value="" disabled>
                                                    섹션 로딩 중...
                                                </MenuItem>
                                            )}
                                            {sortedCategorySections.map((s) => (
                                                <MenuItem key={s.id} value={s.id}>
                                                    {sectionDisplayName(s)}{' '}
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            ml: 1,
                                                            color: 'text.secondary',
                                                            fontSize: '0.75rem',
                                                        }}
                                                    >
                                                        ({s.code})
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    )}
                                />

                                {!isEditMode && (
                                    <TextField
                                        select
                                        label="라이브러리 타입"
                                        fullWidth
                                        margin="normal"
                                        {...register('libraryType')}
                                        defaultValue={LibraryType.SYSTEM}
                                    >
                                        <MenuItem value={LibraryType.SYSTEM}>
                                            시스템
                                        </MenuItem>
                                        <MenuItem value={LibraryType.COMMUNITY}>
                                            커뮤니티
                                        </MenuItem>
                                    </TextField>
                                )}

                                <TextField
                                    label="설명"
                                    fullWidth
                                    margin="normal"
                                    multiline
                                    rows={3}
                                    {...register('description')}
                                    placeholder="설명을 입력하세요"
                                />
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* 썸네일 — Edit 모드: AI 썸네일 패널, Create 모드: 단순 업로드 */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        {isEditMode ? (
                            <LibraryThumbnailPanel
                                libraryId={libraryId}
                                currentThumbnailUrl={thumbnailUrl || undefined}
                                onThumbnailUrlChange={(url) =>
                                    setValue('thumbnailUrl', url ?? '')
                                }
                            />
                        ) : (
                            <Card>
                                <CardContent>
                                    <ImageUpload
                                        value={thumbnailUrl}
                                        onChange={(url) =>
                                            setValue('thumbnailUrl', url)
                                        }
                                        folder="library/thumbnails"
                                        label="썸네일 이미지"
                                        helperText="권장 크기: 800x600px, 최대 5MB"
                                    />
                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        라이브러리 등록 후 AI 썸네일 생성을 사용할 수
                                        있습니다.
                                    </Alert>
                                </CardContent>
                            </Card>
                        )}
                    </Grid>

                    {/* 노출 설정 */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card>
                            <CardContent>
                                <Typography
                                    variant="subtitle1"
                                    gutterBottom
                                    fontWeight="bold"
                                >
                                    노출 설정
                                </Typography>

                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    gap={2}
                                    mt={2}
                                >
                                    <Controller
                                        name="isVisible"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        {...field}
                                                        checked={field.value}
                                                    />
                                                }
                                                label={isEditMode ? '노출' : '즉시 노출'}
                                            />
                                        )}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* 큐레이션 설정 (Create / Edit 공통, 단순화) */}
                    <Grid size={{ xs: 12 }}>
                        <Card>
                            <CardContent>
                                <Typography
                                    variant="subtitle1"
                                    gutterBottom
                                    fontWeight="bold"
                                >
                                    큐레이션 섹션
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    mb={2}
                                >
                                    "(없음)" 선택 시 큐레이션 섹션을 부착하지 않습니다.
                                </Typography>

                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Controller
                                            name="curationSectionId"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    select
                                                    label="큐레이션 섹션"
                                                    fullWidth
                                                    value={field.value ?? 0}
                                                    onChange={(e) =>
                                                        field.onChange(
                                                            Number(e.target.value)
                                                        )
                                                    }
                                                    helperText="library_sections (type=CURATION) 에서 선택"
                                                >
                                                    <MenuItem value={0}>(없음)</MenuItem>
                                                    {sortedCurationSections.map((s) => (
                                                        <MenuItem
                                                            key={s.id}
                                                            value={s.id}
                                                        >
                                                            {sectionDisplayName(s)}{' '}
                                                            <Box
                                                                component="span"
                                                                sx={{
                                                                    ml: 1,
                                                                    color: 'text.secondary',
                                                                    fontSize: '0.75rem',
                                                                }}
                                                            >
                                                                ({s.code})
                                                            </Box>
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            )}
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Controller
                                            name="curationSortOrder"
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    type="number"
                                                    label="큐레이션 정렬 순서"
                                                    fullWidth
                                                    value={field.value ?? ''}
                                                    onChange={(e) => {
                                                        const v = e.target.value;
                                                        field.onChange(
                                                            v === '' ? null : Number(v)
                                                        );
                                                    }}
                                                    helperText="섹션 내 작을수록 먼저 노출"
                                                />
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* 라이브러리 등급 */}
                    {isEditMode && (
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Card>
                                <CardContent>
                                    <Typography
                                        variant="subtitle1"
                                        gutterBottom
                                        fontWeight="bold"
                                    >
                                        라이브러리 등급
                                    </Typography>
                                    <Controller
                                        name="tier"
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                select
                                                label="등급"
                                                fullWidth
                                                value={field.value ?? LibraryTier.FREE}
                                                onChange={field.onChange}
                                                helperText="프리미엄은 구독 사용자만 다운로드할 수 있습니다."
                                            >
                                                {Object.values(LibraryTier).map((t) => (
                                                    <MenuItem key={t} value={t}>
                                                        {LIBRARY_TIER_LABELS[t]}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {/* 버튼 */}
                    <Grid size={{ xs: 12 }}>
                        <Box display="flex" gap={2} justifyContent="flex-end">
                            <Button
                                variant="outlined"
                                onClick={handleCancel}
                                disabled={mutation.isPending}
                            >
                                취소
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={mutation.isPending}
                            >
                                {mutation.isPending
                                    ? `${isEditMode ? '저장' : '생성'} 중...`
                                    : isEditMode
                                      ? '저장'
                                      : '생성'}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </form>
        </Box>
    );
};
